import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Page, Button, Card, Text, BlockStack, Divider, Select, TextField, InlineStack, Box, Badge, Icon, DataTable, Thumbnail, PageActions } from '@shopify/polaris';
import {
  BarcodeIcon,
  ProductIcon,
  AlertCircleIcon,
  DeleteIcon,
  InventoryUpdatedIcon
} from '@shopify/polaris-icons';

import PageFooter from '../partials/PageFooter';
import LoadingSpinner from '../partials/LoadingSpinner';
import SubmittedMessage from "./SubmittedMessage";
import RequiresRefreshBanner from '../partials/RequiresRefreshBanner';

import { addBulkPlaceholderAction, clearBulkActions, publishBulkActions } from '../../redux/actions/bulkInventorySessions';
import { scanInventory } from '../../redux/actions/inventorySessions';
import { locationsIndex } from '../../redux/actions/locations';

const Add = ({ shopUrl,
               payload, 
               bulkInventorySession,
               locations,
               postScanInventory,
               addAction,
               clearSession,
               publishBulkAdd,
               loadLocations,
               requiresRefresh }) => {
  const [location, setLocation] = useState('');
  
  const [submitted, setSubmitted] = useState(false);
  const [scanTerm, setScanTerm] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [bulkChangeValues, setBulkChangeValues] = useState([]);
  const [totalChangeValues, setTotalChangeValues] = useState(0);

  const [locationsList, setLocationsList] = useState([]);

  const [scanFocused, setScanFocused] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(scanTerm !== ''){
        addAction(scanTerm);
        postScanInventory(shopUrl, payload, scanTerm, bulkInventorySession.events.length, location);
        setScanTerm('');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [scanTerm, 500]);

  useEffect(() => {
    clearSession();
    loadLocations(shopUrl, payload);
  }, []);

  useEffect(() => {
    setTotal();
  }, [bulkChangeValues]);


  useEffect(() => {
    if(locations.status === 'success') {

      let tempLocations = [];
      locations.locations.forEach((location) => {
        tempLocations.push({ label: location.name, value: location.id })
      })

      setLocationsList(tempLocations);
    }
  }, [locations]);

  const publishSession = () => {
    let submitCounts = [];

    bulkInventorySession.events.map((event, index) => {
      if(!(event.error)){
        event[0].modifyCount = bulkChangeValues[event[0].node.id];
        submitCounts.push(event);
      }
    })

    publishBulkAdd(shopUrl, payload, submitCounts, changeNotes, location);
    setSubmitted(true);
  }

  const setValue = (value, barcode) => {
    setBulkChangeValues(bulkChangeValues=>({
      ...bulkChangeValues,
      [barcode]: value
    }));
  }

  const setTotal = () => {
    let tempValue = 0;

    for (const [key, value] of Object.entries(bulkChangeValues)) {
      tempValue += (parseInt(value) || 0);
    }

    setTotalChangeValues(tempValue);
  }

  const bulkEventsLoaded = () => {
    let firstLoading = bulkInventorySession.events.find((event) => {
      return event.placeholder
    });

    return !firstLoading;
  }

  const handleLocationSelect = (event) => {
    let matchLocation = locations.locations.find(matchLocation => matchLocation.id === event);
    setLocation(matchLocation);
  }

  const updateScan = (event) => {
    setScanTerm(event);
  }

  const updateNotes = (event) => {
    setChangeNotes(event);
  }

  const validateValue = (val, item) => {
    let value = val.replace(/\D/g,'');
    setValue(value, item);
  }

  const rowImage = (event) => {
    if (event[0]['node']?.product?.media?.edges[0]?.node?.preview?.image) {
      return <Thumbnail
          source={event[0]['node'].product.media.edges[0].node.preview.image.url}
          size="small"
          alt={event[0]['node'].product.media.edges[0].node.preview.image.alt}/>
    }

    return <Icon source={ProductIcon} tone="base" size="small" />
  }

  const optionBadge = (option) => {
    return <div key={option.name}>
      <Badge 
        tone="info"
        key={option.name}
      >
        <Text>{option.value}</Text>
      </Badge>
       &nbsp;
    </div>
  }

  const eventRow = (rowItem, index) => {
    if(rowItem.error) {
      return [
        <Icon source={AlertCircleIcon} tone="critical" />,
        `${ rowItem.error }`,
        '',
        '',
        '',
        '',
        ''
      ]
    }

    if(rowItem.placeholder) {
      return [
        <LoadingSpinner size="small" />,
        `Scanned value: ${rowItem.sku}`,
        '',
        '',
        '',
        '',
        ''
      ]
    }

    let options = rowItem[0]['node'].selectedOptions;

    return [
      rowImage(rowItem),
      <Text as="strong" fontWeight="semibold">{rowItem[0]['node'].displayName}</Text>,
      rowItem[0]['node'].sku,
      rowItem[0]['node'].barcode,
      (options.length > 0 ? options.map((option) => optionBadge(option)) : ''),
      rowItem[0]['node'].inventoryItem?.inventoryLevel?.quantities[0]?.quantity || 'N/A',
      <TextField
        value={bulkChangeValues[rowItem[0]?.node?.id]}
        onChange={(event) => validateValue(event, rowItem[0]?.node?.id)}
        autoComplete="off"
        placeholder="Update Quantity"
        disabled={submitted}
        type="number"
        autoSize
      />
    ]
  }

  return(
    <Page
      title="Add Inventory"
      primaryAction={<Button icon={InventoryUpdatedIcon} variant="primary" onClick={() => publishSession()} disabled={submitted} loading={submitted && bulkInventorySession.status !== 'success'}>Submit Changes</Button>}
      secondaryActions={<Button icon={DeleteIcon} onClick={() => clearSession()} disabled={submitted}>Clear Session</Button>}
      backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
    >
      { requiresRefresh &&
        <RequiresRefreshBanner />
      }
      <Card>
        <BlockStack gap="500">
          { locations.status !== 'success' &&
            <LoadingSpinner size="small" statusText="Loading locations" />
          }

          { locations.status === 'success' &&
            <Select
              label="Locations"
              options={locationsList}
              onChange={handleLocationSelect}
              value={location?.id}
              placeholder="Select location"
              disabled={submitted}
            />
          }

          <Divider />

          <InlineStack blockAlign="center">
            <div style={{ 'width': '50%' }}>
              <TextField
                label="Scan Barcode (SKU, UPC, EAN)"
                value={scanTerm}
                onChange={updateScan}
                autoComplete="off"
                placeholder={ scanFocused ? "Scan barcode" : "Click here to begin scanning" }
                prefix={<Badge tone={ scanFocused ? 'success' : 'critical' }>{ scanFocused ? 'Ready' : 'Not ready' }</Badge>}
                onFocus={() => setScanFocused(true)}
                onBlur={() => setScanFocused(false)}
                disabled={ !location || submitted }
                connectedLeft={
                  <Icon
                    source={BarcodeIcon}
                    tone="base"
                  />
                }
              />
            </div>
          </InlineStack>
        </BlockStack>
      </Card>

      <Box paddingBlockStart="200">
        <Card>
          <BlockStack gap="200">
            <TextField
              label="Notes"
              value={changeNotes}
              onChange={updateNotes}
              multiline={4}
              autoComplete="off"
              disabled={submitted}
            />

            { submitted && bulkInventorySession.status === 'success' &&
              <SubmittedMessage />
            }
          </BlockStack>
        </Card>
      </Box>

      <Box paddingBlockStart="200">
        <Card>
          <DataTable
            headings={[
              'Image',
              'Name',
              'SKU',
              'Barcode',
              'Options',
              'Current',
              'Update Quantity'
            ]}
            columnContentTypes={[
              'text',
              'text',
              'text',
              'text',
              'text',
              'numeric',
              'numeric'
            ]}
            hasZebraStripingOnData
            rows={bulkInventorySession.events.toReversed().map((event, index) => eventRow(event, index))}
          />
        </Card>
      </Box>

      <PageActions
        primaryAction={<Button icon={InventoryUpdatedIcon} variant="primary" onClick={() => publishSession()} disabled={submitted} loading={submitted && bulkInventorySession.status !== 'success'}>Submit Changes</Button>}
        secondaryActions={<Button icon={DeleteIcon} onClick={() => clearSession()} disabled={submitted}>Clear Session</Button>}
      />

      <PageFooter stripeBackground='#2c9c7c' helpLink="inventory-management/add-inventory-3/" helpDescription="adding to product inventory" />
    </Page>
  )
}

const mapStateToProps = state => ({
  requiresRefresh: state.activeToken.requiresRefresh,
  bulkInventorySession: state.bulkInventorySessions,
  locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  postScanInventory: (shopUrl, payload, barcode, actionId, activeLocation) => dispatch(scanInventory(shopUrl, payload, barcode, actionId, activeLocation)),
  addAction: (barcode, actionType) => dispatch(addBulkPlaceholderAction(barcode)),
  clearSession: () => dispatch(clearBulkActions()),
  publishBulkAdd: (shopUrl, payload, actions, comments, activeLocation) => dispatch(publishBulkActions(shopUrl, payload, 'add', actions, comments, activeLocation)),  
  loadLocations: (shopUrl, payload) => dispatch(locationsIndex(shopUrl, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Add);
